<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import whatsappTemplatesAPI from 'dashboard/api/whatsappTemplates';

const router = useRouter();
const store = useStore();
const { t } = useI18n();
const { accountScopedRoute } = useAccount();

const inboxes = useMapGetter('inboxes/getInboxes');
const currentUser = useMapGetter('getCurrentUser');

const isAdmin = computed(() => currentUser.value?.role === 'administrator');

const whatsappCloudInboxes = computed(() =>
  (inboxes.value || []).filter(
    i => i.channel_type === 'Channel::Whatsapp' && i.provider === 'whatsapp_cloud'
  )
);

const selectedInboxId = ref(null);
const templates = ref([]);
const loading = ref(false);
const error = ref(null);
const statusFilter = ref('');
const search = ref('');
const confirmDelete = ref(null);
const deleting = ref(false);
const deleteError = ref(null);

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'PAUSED', label: 'Paused' },
];

const CATEGORY_ICONS = {
  MARKETING: 'i-lucide-megaphone',
  UTILITY: 'i-lucide-wrench',
  AUTHENTICATION: 'i-lucide-shield-check',
};

const STATUS_CLASSES = {
  APPROVED: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  PENDING: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  REJECTED: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  PAUSED: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
  DISABLED: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
};

const filteredTemplates = computed(() => {
  let list = templates.value;
  if (statusFilter.value) list = list.filter(t => t.status === statusFilter.value);
  if (search.value.trim()) {
    const q = search.value.toLowerCase();
    list = list.filter(t => t.name.toLowerCase().includes(q));
  }
  return list;
});

const selectedInbox = computed(() =>
  whatsappCloudInboxes.value.find(i => i.id === selectedInboxId.value)
);

async function fetchTemplates() {
  if (!selectedInboxId.value) return;
  loading.value = true;
  error.value = null;
  templates.value = [];
  try {
    const { data } = await whatsappTemplatesAPI.list(selectedInboxId.value, statusFilter.value || null);
    templates.value = data.templates || [];
  } catch (e) {
    error.value = e.response?.data?.error || 'Error loading templates';
  } finally {
    loading.value = false;
  }
}

async function handleDelete(template) {
  deleting.value = true;
  deleteError.value = null;
  try {
    await whatsappTemplatesAPI.remove(template.id, template.name, selectedInboxId.value);
    confirmDelete.value = null;
    await fetchTemplates();
  } catch (e) {
    deleteError.value = e.response?.data?.error || 'Error deleting template';
  } finally {
    deleting.value = false;
  }
}

function getBodyText(template) {
  const body = (template.components || []).find(c => c.type === 'BODY');
  return body?.text || '';
}

function getHeaderText(template) {
  const header = (template.components || []).find(c => c.type === 'HEADER');
  if (!header) return null;
  if (header.format === 'TEXT') return header.text;
  return header.format; // IMAGE / VIDEO / DOCUMENT
}

function hasButtons(template) {
  return (template.components || []).some(c => c.type === 'BUTTONS');
}

function getButtons(template) {
  const btn = (template.components || []).find(c => c.type === 'BUTTONS');
  return btn?.buttons || [];
}

watch(selectedInboxId, fetchTemplates);
watch(statusFilter, fetchTemplates);

onMounted(() => {
  store.dispatch('inboxes/get');
  if (whatsappCloudInboxes.value.length > 0) {
    selectedInboxId.value = whatsappCloudInboxes.value[0].id;
  }
});

watch(whatsappCloudInboxes, inboxList => {
  if (!selectedInboxId.value && inboxList.length > 0) {
    selectedInboxId.value = inboxList[0].id;
  }
});

function navigateToBuilder() {
  router.push({ name: 'whatsapp_templates_new' });
}
</script>

<template>
  <div class="flex flex-col flex-1 h-full w-full min-w-0 bg-n-background overflow-hidden">
    <!-- Header -->
    <div class="flex items-center justify-between px-6 py-4 border-b border-n-weak bg-n-solid-1">
      <div>
        <h1 class="text-xl font-semibold text-n-slate-12">
          Meta Templates
        </h1>
        <p class="text-sm text-n-slate-11 mt-0.5">
          Manage WhatsApp Business message templates
        </p>
      </div>
      <div class="flex items-center gap-3">
        <!-- Sync button -->
        <button
          id="btn-sync-templates"
          class="flex items-center gap-2 px-3 py-2 bg-n-solid-2 hover:bg-n-solid-3 border border-n-weak text-n-slate-12 text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
          :disabled="loading"
          title="Sincronizar plantillas con Meta"
          @click="fetchTemplates"
        >
          <span :class="['i-lucide-refresh-cw size-4 text-n-slate-11', { 'animate-spin': loading }]" />
          <span>Sincronizar</span>
        </button>

        <button
          v-if="isAdmin"
          id="btn-new-template"
          class="flex items-center gap-2 px-4 py-2 bg-woot-600 hover:bg-woot-700 text-white text-sm font-medium rounded-lg transition-colors"
          @click="navigateToBuilder"
        >
          <span class="i-lucide-plus size-4" />
          New Template
        </button>
      </div>
    </div>

    <!-- Toolbar -->
    <div class="flex flex-wrap items-center justify-between gap-4 px-6 py-3.5 border-b border-n-weak bg-n-solid-1">
      <div class="flex flex-wrap items-center gap-3">
        <!-- Inbox selector -->
        <div class="relative flex items-center min-w-[200px]">
          <select
            id="inbox-selector"
            v-model="selectedInboxId"
            class="w-full text-sm border border-n-weak rounded-xl px-3.5 pr-8 h-9 bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500/50 [appearance:none] [-webkit-appearance:none] bg-none font-medium cursor-pointer transition-colors hover:border-n-slate-8"
          >
            <option
              v-for="inbox in whatsappCloudInboxes"
              :key="inbox.id"
              :value="inbox.id"
            >
              {{ inbox.name }}
            </option>
          </select>
          <div class="absolute right-2.5 inset-y-0 flex items-center justify-center text-n-slate-10 pointer-events-none z-10">
            <span class="i-lucide-chevron-down size-4" />
          </div>
        </div>

        <!-- Status filter -->
        <div class="flex items-center gap-1 p-1 bg-n-solid-2 border border-n-weak rounded-xl">
          <button
            v-for="opt in STATUS_OPTIONS"
            :key="opt.value"
            :class="[
              'px-3 h-7 text-xs font-medium rounded-lg transition-all',
              statusFilter === opt.value
                ? 'bg-woot-600 text-white shadow-sm'
                : 'text-n-slate-11 hover:text-n-slate-12 hover:bg-n-background'
            ]"
            @click="statusFilter = opt.value"
          >
            {{ opt.label }}
          </button>
        </div>
      </div>

      <!-- Search -->
      <div class="relative flex items-center ml-auto">
        <input
          id="template-search"
          v-model="search"
          type="text"
          placeholder="Search templates..."
          class="px-3.5 h-9 text-sm border border-n-weak rounded-xl bg-n-background text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:ring-2 focus:ring-woot-500/50 w-60 transition-colors hover:border-n-slate-8"
        />
      </div>
    </div>

    <!-- No inbox -->
    <div
      v-if="whatsappCloudInboxes.length === 0"
      class="flex flex-col items-center justify-center flex-1 gap-4 text-n-slate-10"
    >
      <span class="i-lucide-smartphone size-12 opacity-40" />
      <p class="text-sm">No WhatsApp Cloud API inboxes found</p>
    </div>

    <!-- Loading -->
    <div
      v-else-if="loading"
      class="flex items-center justify-center flex-1 gap-3 text-n-slate-10"
    >
      <span class="i-lucide-loader-2 size-5 animate-spin" />
      <span class="text-sm">Loading templates from Meta...</span>
    </div>

    <!-- Error -->
    <div
      v-else-if="error"
      class="flex flex-col items-center justify-center flex-1 gap-3 text-red-500"
    >
      <span class="i-lucide-alert-circle size-8" />
      <p class="text-sm">{{ error }}</p>
      <button
        class="px-4 py-2 text-sm bg-n-background border border-n-weak rounded-lg hover:bg-n-solid-1 text-n-slate-12"
        @click="fetchTemplates"
      >
        Retry
      </button>
    </div>

    <!-- Empty state -->
    <div
      v-else-if="filteredTemplates.length === 0"
      class="flex flex-col items-center justify-center flex-1 gap-4 text-n-slate-10"
    >
      <span class="i-lucide-layout-template size-12 opacity-40" />
      <p class="text-sm font-medium">No templates found</p>
      <p class="text-xs text-center max-w-xs opacity-70">
        {{ search ? 'Try a different search term' : 'Create your first WhatsApp template to get started' }}
      </p>
      <button
        v-if="isAdmin && !search"
        class="flex items-center gap-2 px-4 py-2 bg-woot-600 hover:bg-woot-700 text-white text-sm font-medium rounded-lg"
        @click="navigateToBuilder"
      >
        <span class="i-lucide-plus size-4" />
        Create Template
      </button>
    </div>

    <!-- Template grid -->
    <div
      v-else
      class="flex-1 overflow-y-auto p-6"
    >
      <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        <div
          v-for="tpl in filteredTemplates"
          :key="tpl.id"
          class="relative flex flex-col bg-n-solid-1 border border-n-weak rounded-xl overflow-hidden hover:border-woot-400 transition-colors group"
        >
          <!-- Card header -->
          <div class="flex items-start justify-between px-4 pt-4 pb-2 gap-2">
            <div class="flex items-center gap-2 min-w-0">
              <span
                :class="[CATEGORY_ICONS[tpl.category] || 'i-lucide-file-text', 'size-4 text-woot-500 shrink-0']"
              />
              <span class="text-sm font-semibold text-n-slate-12 truncate">{{ tpl.name }}</span>
            </div>
            <span
              :class="['shrink-0 px-2 py-0.5 text-xs font-medium rounded-full', STATUS_CLASSES[tpl.status] || STATUS_CLASSES.DISABLED]"
            >
              {{ tpl.status }}
            </span>
          </div>

          <!-- Meta info -->
          <div class="flex items-center gap-3 px-4 pb-3 text-xs text-n-slate-10">
            <span class="flex items-center gap-1">
              <span class="i-lucide-globe size-3" />
              {{ tpl.language }}
            </span>
            <span class="flex items-center gap-1">
              <span class="i-lucide-tag size-3" />
              {{ tpl.category }}
            </span>
          </div>

          <!-- Divider -->
          <div class="mx-4 border-t border-n-weak" />

          <!-- WhatsApp-like preview -->
          <div class="flex-1 px-4 py-3">
            <div class="bg-[#e5ddd5] dark:bg-[#1a1a2e] rounded-lg p-2 text-xs font-sans">
              <!-- Mock WA UI -->
              <div class="bg-white dark:bg-[#202c33] rounded-lg p-2.5 shadow-sm max-w-[240px] ml-auto">
                <!-- Header -->
                <div
                  v-if="getHeaderText(tpl)"
                  class="mb-1"
                >
                  <span
                    v-if="!['IMAGE','VIDEO','DOCUMENT'].includes(getHeaderText(tpl))"
                    class="block font-semibold text-gray-800 dark:text-gray-100 text-xs leading-tight"
                  >
                    {{ getHeaderText(tpl) }}
                  </span>
                  <div
                    v-else
                    class="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 rounded px-2 py-1 text-gray-500 dark:text-gray-400"
                  >
                    <span
                      :class="getHeaderText(tpl) === 'IMAGE' ? 'i-lucide-image' : getHeaderText(tpl) === 'VIDEO' ? 'i-lucide-video' : 'i-lucide-file'"
                      class="size-3"
                    />
                    <span class="text-[10px]">{{ getHeaderText(tpl) }}</span>
                  </div>
                </div>

                <!-- Body -->
                <p class="text-gray-800 dark:text-gray-100 text-xs leading-snug whitespace-pre-line line-clamp-4">
                  {{ getBodyText(tpl) || '(No body text)' }}
                </p>

                <!-- Timestamp mock -->
                <div class="flex justify-end mt-1">
                  <span class="text-[9px] text-gray-400">4:30 PM ✓✓</span>
                </div>

                <!-- Buttons -->
                <div
                  v-if="hasButtons(tpl)"
                  class="mt-1.5 pt-1.5 border-t border-gray-100 dark:border-gray-600 flex flex-col gap-1"
                >
                  <div
                    v-for="btn in getButtons(tpl)"
                    :key="btn.text"
                    class="text-center text-[10px] text-blue-500 font-medium py-0.5"
                  >
                    {{ btn.text }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Actions -->
          <div
            v-if="isAdmin"
            class="flex items-center justify-end gap-2 px-4 py-2.5 border-t border-n-weak bg-n-background"
          >
            <button
              :id="'btn-delete-' + tpl.id"
              class="flex items-center gap-1 px-2.5 py-1 text-xs text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
              :disabled="tpl.status === 'APPROVED'"
              :title="tpl.status === 'APPROVED' ? 'Cannot delete approved templates via API' : 'Delete template'"
              @click="confirmDelete = tpl"
            >
              <span class="i-lucide-trash-2 size-3.5" />
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Confirm delete modal -->
    <div
      v-if="confirmDelete"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
    >
      <div class="bg-n-solid-1 rounded-2xl shadow-2xl p-6 max-w-sm w-full mx-4 border border-n-weak">
        <div class="flex items-center gap-3 mb-4">
          <span class="i-lucide-alert-triangle size-6 text-red-500" />
          <h3 class="text-base font-semibold text-n-slate-12">Delete Template</h3>
        </div>
        <p class="text-sm text-n-slate-11 mb-2">
          Are you sure you want to delete <strong class="text-n-slate-12">{{ confirmDelete.name }}</strong>?
        </p>
        <p class="text-xs text-n-slate-10 mb-5">
          This action is permanent and cannot be undone.
        </p>
        <p
          v-if="deleteError"
          class="text-xs text-red-500 mb-3"
        >
          {{ deleteError }}
        </p>
        <div class="flex gap-3 justify-end">
          <button
            class="px-4 py-2 text-sm border border-n-weak rounded-lg hover:bg-n-solid-2 text-n-slate-11 transition-colors"
            @click="confirmDelete = null; deleteError = null"
          >
            Cancel
          </button>
          <button
            class="flex items-center gap-2 px-4 py-2 text-sm bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50"
            :disabled="deleting"
            @click="handleDelete(confirmDelete)"
          >
            <span
              v-if="deleting"
              class="i-lucide-loader-2 size-3.5 animate-spin"
            />
            Delete
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
